
import React, { useState } from 'react';
import { useReferredUsers, useAffiliateInfo } from '@/hooks/useAffiliate';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, UserPlus } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';

const AffiliateReferrals = () => {
  const [filter, setFilter] = useState<'all' | 'registered' | 'converted'>('all');
  const { data: referredUsers, isLoading, error } = useReferredUsers(filter);
  const { data: affiliateInfo } = useAffiliateInfo();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const copyReferralLink = () => {
    if (!affiliateInfo?.referral_code) {
      toast({
        title: "Error",
        description: "Could not find your referral code. Please try again later.",
        variant: "destructive"
      });
      return;
    }
    
    const baseUrl = window.location.origin;
    const referralUrl = `${baseUrl}/?ref=${affiliateInfo.referral_code}`;
    
    navigator.clipboard.writeText(referralUrl)
      .then(() => {
        toast({
          title: "Copied to clipboard",
          description: "Your referral link has been copied to clipboard."
        });
      })
      .catch((err) => {
        toast({
          title: "Failed to copy",
          description: "Could not copy to clipboard. Please try again.",
          variant: "destructive"
        });
        console.error('Failed to copy:', err);
      });
  };

  if (error) {
    console.error('Error loading referred users:', error);
    return (
      <div className={`space-y-${isMobile ? '2' : '4'} ${isMobile ? 'px-4 py-2' : ''}`}>
        <h1 className={`${isMobile ? 'text-base' : 'text-xl md:text-3xl'} font-bold tracking-tight`}>Referred Users</h1>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription className={isMobile ? "text-xs" : "text-sm"}>
            Unable to load referred users. Please try again later.
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-2 text-xs">
                <summary>Error details (visible in development only)</summary>
                <pre className="mt-2 whitespace-pre-wrap">{error instanceof Error ? error.message : String(error)}</pre>
              </details>
            )}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className={`space-y-${isMobile ? '2' : '4 md:space-y-6'} ${isMobile ? 'px-4 py-2' : ''}`}>
      <div>
        <h1 className={`${isMobile ? 'text-base' : 'text-xl md:text-3xl'} font-bold tracking-tight`}>
          Referred Users
        </h1>
        <p className={`${isMobile ? "text-xs mt-0.5" : "text-sm text-muted-foreground mt-2"} text-muted-foreground`}>
          Track users who registered using your referral links
        </p>
      </div>

      <Card>
        <CardHeader className={isMobile ? "p-2.5" : ""}>
          <CardTitle className={isMobile ? "text-sm" : "text-2xl"}>Referred Users Overview</CardTitle>
          <CardDescription className={isMobile ? "text-xs leading-tight" : "text-sm"}>
            Users who signed up using your referral links. Personal information is masked for privacy.
          </CardDescription>
        </CardHeader>
        <CardContent className={isMobile ? "p-2.5 pt-0" : ""}>
          <Tabs defaultValue="all" className="w-full" onValueChange={(value) => setFilter(value as any)}>
            <TabsList className={`${isMobile ? "w-full grid grid-cols-3 h-7 mb-2" : "mb-4"}`}>
              <TabsTrigger value="all" className={isMobile ? "text-xs py-0.5 px-2" : ""}>All</TabsTrigger>
              <TabsTrigger value="registered" className={isMobile ? "text-xs py-0.5 px-2" : ""}>Registered</TabsTrigger>
              <TabsTrigger value="converted" className={isMobile ? "text-xs py-0.5 px-2" : ""}>Converted</TabsTrigger>
            </TabsList>
            <TabsContent value="all" className="w-full">
              {renderUserTable(referredUsers, isLoading, copyReferralLink, isMobile)}
            </TabsContent>
            <TabsContent value="registered" className="w-full">
              {renderUserTable(referredUsers, isLoading, copyReferralLink, isMobile)}
            </TabsContent>
            <TabsContent value="converted" className="w-full">
              {renderUserTable(referredUsers, isLoading, copyReferralLink, isMobile)}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

const renderUserTable = (users: any[] | undefined, isLoading: boolean, copyReferralLink: () => void, isMobile: boolean) => {
  if (isLoading) {
    return (
      <div className={`space-y-${isMobile ? '1.5' : '3'}`}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className={`w-full ${isMobile ? 'h-8' : 'h-12'}`} />
        ))}
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center ${isMobile ? 'py-3' : 'py-6 md:py-12'} text-center`}>
        <div className={`bg-muted/30 ${isMobile ? 'p-1.5' : 'p-3 md:p-4'} rounded-full ${isMobile ? 'mb-1.5' : 'mb-3 md:mb-4'}`}>
          <UserPlus className={`${isMobile ? "h-4 w-4" : "h-6 w-6 md:h-8 md:w-8"} text-muted-foreground`} />
        </div>
        <h3 className={`font-medium ${isMobile ? "text-xs" : "text-base md:text-lg"}`}>No referred users yet</h3>
        <p className={`${isMobile ? "text-xs max-w-xs leading-tight" : "text-sm max-w-md"} text-muted-foreground ${isMobile ? 'mt-0.5' : 'mt-1 md:mt-2'}`}>
          Share your referral link to start earning commission.
        </p>
        <Button 
          variant="outline" 
          size={isMobile ? "sm" : "default"} 
          className={`${isMobile ? 'mt-2 h-7 text-xs px-3' : 'mt-4 md:mt-6'}`} 
          onClick={copyReferralLink}
        >
          Copy Link
        </Button>
      </div>
    );
  }

  if (isMobile) {
    // Compact card-based layout for mobile
    return (
      <div className="space-y-2">
        {users.map((user) => (
          <Card key={user.id} className="overflow-hidden">
            <CardContent className="p-2.5">
              <div className="flex justify-between items-start mb-1.5">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-xs truncate">{user.masked_name}</div>
                  <div className="text-xs text-muted-foreground truncate leading-tight">{user.masked_email}</div>
                </div>
                <StatusBadge status={user.status} mobile />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mt-1.5">
                <div>
                  <span className="text-muted-foreground block">Date</span>
                  <div className="font-mono text-xs leading-tight">{new Date(user.created_at).toLocaleDateString()}</div>
                </div>
                <div>
                  <span className="text-muted-foreground block">Code</span>
                  <div className="font-mono text-xs truncate leading-tight">{user.referral_code}</div>
                </div>
              </div>
              {user.product && (
                <div className="text-xs mt-1.5">
                  <span className="text-muted-foreground">Product: </span>
                  <span className="text-xs">{user.product}</span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Standard table for desktop with better responsive design
  return (
    <div className="overflow-auto">
      <Table>
        <TableCaption className="text-xs">List of users referred by you</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs">Date</TableHead>
            <TableHead className="text-xs">User</TableHead>
            <TableHead className="text-xs">Status</TableHead>
            <TableHead className="text-xs">Code</TableHead>
            <TableHead className="text-xs">Product</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell className="text-xs">{new Date(user.created_at).toLocaleDateString()}</TableCell>
              <TableCell>
                <div>
                  <div className="font-medium text-sm">{user.masked_name}</div>
                  <div className="text-xs text-muted-foreground">{user.masked_email}</div>
                </div>
              </TableCell>
              <TableCell>
                <StatusBadge status={user.status} />
              </TableCell>
              <TableCell className="font-mono text-xs">{user.referral_code}</TableCell>
              <TableCell className="text-xs">{user.product || 'Standard'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const StatusBadge = ({ status, mobile = false }: { status: string; mobile?: boolean }) => {
  const badgeClass = mobile ? "text-xs px-1.5 py-0.5" : "";
  
  switch (status) {
    case 'converted':
      return <Badge className={`bg-green-500 ${badgeClass}`}>Converted</Badge>;
    case 'registered':
      return <Badge variant="outline" className={badgeClass}>Registered</Badge>;
    default:
      return <Badge variant="secondary" className={badgeClass}>{status}</Badge>;
  }
};

export default AffiliateReferrals;
